import React, { useState } from "react";
import { toast } from "react-toastify";
import api from "../../../services/api";

const VoucherRedeemPage = () => {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // { type: 'success' | 'error', message: string, detail?: string }

  const handleCodeChange = (e) => {
    setCode(e.target.value.toUpperCase());
    if (status) setStatus(null); // Clear status on edit
  };

  const handleRedeem = async (e) => {
    e.preventDefault();
    if (!code.trim()) {
      setStatus({ type: "error", message: "Please enter a code" });
      return;
    }

    setLoading(true);
    setStatus(null);

    try {
      const response = await api.post("/vouchers/redeem", { code });

      if (response.data.success) {
        setStatus({
          type: "success",
          message: response.data.message,
          detail: response.data.data?.denomination
            ? `Value: ₹${response.data.data.denomination}`
            : null,
        });
        setCode(""); // Clear input on success
        toast.success("Voucher redeemed successfully!");
      } else {
        // Should be caught by catch block usually if status is not 2xx,
        // but just in case api returns 200 with success: false
        setStatus({
          type: "error",
          message: response.data.message || "Redemption failed",
        });
      }
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        "Failed to redeem voucher. Please try again.";
      setStatus({ type: "error", message: msg });
      // Toast is handled by api interceptor for some errors, but specific feedback here is good.
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-12 flex flex-col items-center justify-center min-h-[60vh]">
      <div className="w-full max-w-md bg-white/10 backdrop-blur-md rounded-xl p-8 shadow-2xl border border-white/20">
        <h1 className="text-3xl font-bold text-center mb-6 text-white">
          Redeem Voucher
        </h1>

        <form onSubmit={handleRedeem} className="flex flex-col gap-4">
          <div className="relative">
            <label htmlFor="voucherCode" className="sr-only">
              Voucher Code
            </label>
            <input
              id="voucherCode"
              type="text"
              value={code}
              onChange={handleCodeChange}
              placeholder="ENTER CODE (e.g. WELCOME50)"
              className="w-full px-4 py-3 bg-black/20 border border-white/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/50 transition-all text-center text-xl tracking-wider font-mono uppercase"
              disabled={loading}
              autoComplete="off"
            />
          </div>

          <button
            type="submit"
            disabled={loading || !code.trim()}
            className={`w-full py-3 px-6 rounded-lg font-bold text-white transition-all transform hover:scale-[1.02] active:scale-[0.98] ${
              loading || !code.trim()
                ? "bg-gray-600 cursor-not-allowed opacity-50"
                : "bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 shadow-lg hover:shadow-purple-500/30"
            }`}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                Processing...
              </span>
            ) : (
              "Redeem Now"
            )}
          </button>
        </form>

        {/* Status Feedback Area */}
        {status && (
          <div
            className={`mt-6 p-4 rounded-lg border flex flex-col items-center text-center animate-fade-in ${
              status.type === "success"
                ? "bg-green-500/20 border-green-500/50 text-green-100"
                : "bg-red-500/20 border-red-500/50 text-red-100"
            }`}
          >
            <span className="text-2xl mb-2">
              {status.type === "success" ? "🎉" : "⚠️"}
            </span>
            <p className="font-semibold">{status.message}</p>
            {status.detail && (
              <p className="text-sm opacity-90 mt-1">{status.detail}</p>
            )}
          </div>
        )}

        <div className="mt-8 text-center text-sm text-gray-400">
          <p>
            Have a gift code? Enter it above to add credits to your account
            instantly.
          </p>
        </div>
      </div>
    </div>
  );
};

export default VoucherRedeemPage;
